import React from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { TrackableItem, TrackableList } from 'types/trackables';
import { isAPIError } from 'types/api_error';

import {
  useCreateTrackableItem,
  useCreateTrackableList,
  useTrackableItems,
  useTrackableLists,
} from 'trackables/hooks';
import { useTrackableFiles } from 'trackables/hooks/useTrackableFiles';
import { useUploadTrackableFile } from 'trackables/hooks/useUploadTrackableFile';

import {
  addErrorNotification,
  addNotification,
} from 'services/notification';

import * as Dialog from 'components/Dialog';
import { Button } from 'components/Button/Button';
import { InlineNotification } from 'components/InlineNotification/InlineNotification';
import { Field } from 'components/Field/Field';
import { Heading } from 'components/Heading/Heading';
import { Input } from 'components/Input/Input';
import { Pill } from 'components/Pill/Pill';
import { Radio, RadioOption } from 'components/Radio/Radio';
import { Select } from 'components/Select/Select';
import { Text } from 'components/Text/Text';

type AddFaceToWatchlistDialogProps = {
  open: boolean;
  onClose: () => void;
  /** The cropped face image to add as a reference file */
  faceCrop: File;
  /** Data URL preview of the face crop */
  facePreviewUrl: string;
};

type ListMode = 'existing' | 'new';
type ItemMode = 'existing' | 'new';

export function AddFaceToWatchlistDialog({
  open,
  onClose,
  faceCrop,
  facePreviewUrl,
}: AddFaceToWatchlistDialogProps) {
  const [listMode, setListMode] = React.useState<ListMode>('existing');
  const [itemMode, setItemMode] = React.useState<ItemMode>('new');

  const [selectedList, setSelectedList] = React.useState<TrackableList | null>(
    null
  );
  const [selectedItem, setSelectedItem] = React.useState<TrackableItem | null>(
    null
  );
  const [newListName, setNewListName] = React.useState('');
  const [newItemName, setNewItemName] = React.useState('');
  const [newItemDescription, setNewItemDescription] = React.useState('');
  const [newItemLabels, setNewItemLabels] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Reset state whenever the dialog opens with a fresh face
  React.useEffect(() => {
    if (open) {
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const { data: faceListsData, isLoading: isLoadingLists } = useTrackableLists({
    params: { object_types: ['face'], limit: 100 },
    enabled: open,
  });

  const faceLists = React.useMemo(
    () => faceListsData?.data ?? [],
    [faceListsData]
  );

  // Default to existing mode when lists exist, new mode otherwise
  React.useEffect(() => {
    if (!open) return;
    if (!isLoadingLists && faceLists.length === 0) {
      setListMode('new');
    }
  }, [open, isLoadingLists, faceLists.length]);

  const { data: itemsData, isLoading: isLoadingItems } = useTrackableItems({
    params: {
      trackable_list_id: selectedList?.id,
      limit: 100,
    },
    enabled: open && listMode === 'existing' && Boolean(selectedList?.id),
  });

  const items = React.useMemo(() => itemsData?.data ?? [], [itemsData]);

  // Reference faces already on the selected item so the user can see
  // existing examples before adding a new one.
  const showExistingFiles =
    open &&
    listMode === 'existing' &&
    itemMode === 'existing' &&
    Boolean(selectedList?.id) &&
    Boolean(selectedItem?.id);

  const { data: existingFilesData, isLoading: isLoadingExistingFiles } =
    useTrackableFiles({
      params: {
        trackable_list_id: selectedList?.id ?? '',
        trackable_item_id: selectedItem?.id ?? '',
        with_thumbnail: true,
        limit: 20,
      },
      enabled: showExistingFiles,
    });

  const existingFiles = React.useMemo(
    () => existingFilesData?.data ?? [],
    [existingFilesData]
  );

  const queryClient = useQueryClient();
  const { mutateAsync: createTrackableList } = useCreateTrackableList();
  const { mutateAsync: createTrackableItem } = useCreateTrackableItem();
  const { uploadTrackableFile } = useUploadTrackableFile();

  function resetForm() {
    setSelectedList(null);
    setSelectedItem(null);
    setNewListName('');
    setNewItemName('');
    setNewItemDescription('');
    setNewItemLabels('');
    setListMode('existing');
    setItemMode('new');
    setError(null);
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    // Validation
    if (listMode === 'existing' && !selectedList) {
      setError('Please select a watchlist.');
      return;
    }
    if (listMode === 'new' && !newListName.trim()) {
      setError('Please enter a name for the new watchlist.');
      return;
    }
    if (listMode === 'existing' && itemMode === 'existing' && !selectedItem) {
      setError('Please select an item.');
      return;
    }
    if (itemMode === 'new' && !newItemName.trim()) {
      setError('Please enter a name for the new item.');
      return;
    }

    setIsSubmitting(true);
    let step = '';

    try {
      step = 'creating watchlist';
      let listId: string;
      if (listMode === 'new') {
        const createdList = await createTrackableList({
          name: newListName.trim(),
          object_type: 'face',
        });
        listId = createdList.id;
      } else {
        listId = selectedList!.id;
      }

      step = 'creating watchlist item';
      let itemId: string;
      if (itemMode === 'new') {
        const labels = newItemLabels
          .split(',')
          .map((label) => label.trim())
          .filter(Boolean);
        const createdItem = await createTrackableItem({
          trackable_list_id: listId,
          name: newItemName.trim(),
          description: newItemDescription.trim() || undefined,
          labels: labels.length > 0 ? labels : undefined,
        });
        itemId = createdItem.id;
      } else {
        itemId = selectedItem!.id;
      }

      step = 'uploading reference image';
      await uploadTrackableFile(faceCrop, {
        trackable_list_id: listId,
        trackable_item_id: itemId,
        name: faceCrop.name || 'face-crop.png',
        size: faceCrop.size,
      });

      queryClient.invalidateQueries(['trackable-items']);
      queryClient.invalidateQueries(['trackable-files']);

      addNotification({
        title: 'Face added to watchlist',
        variant: 'success',
      });
      handleClose();
    } catch (err) {
      const detail =
        err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed while ${step}: ${detail}`);
      addErrorNotification({
        title: `Failed while ${step}`,
        error: isAPIError(err) ? err : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const canUseExistingLists = !isLoadingLists && faceLists.length > 0;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
      className="min-w-md max-w-lg"
    >
      <Dialog.Title asChild>
        <Heading level="2">Add face to watchlist</Heading>
      </Dialog.Title>

      <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="h-[96px] w-[96px] shrink-0 overflow-hidden rounded-md border border-neutral-300 dark:border-neutral-700">
            <img
              src={facePreviewUrl}
              alt="Face to add"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <Text type="paragraph">
              Add this face as a reference image to a watchlist item. It will be
              used to identify this person in video streams.
            </Text>
            <InlineNotification intent="info">
              For best matching results, use a clear, front-facing photo of the
              face.
            </InlineNotification>
          </div>
        </div>

        <Field label="Watchlist">
          <div className="flex flex-col gap-2">
            <Radio
              id="add-face-list-mode"
              value={listMode}
              onChange={(value) => {
                if (value === 'existing' || value === 'new') {
                  setListMode(value);
                  if (value === 'new') {
                    setItemMode('new');
                    setSelectedItem(null);
                  }
                }
              }}
              disabled={isSubmitting}
            >
              <RadioOption value="existing" disabled={!canUseExistingLists}>
                Existing watchlist
              </RadioOption>
              <RadioOption value="new">New watchlist</RadioOption>
            </Radio>

            {listMode === 'existing' ? (
              <Select<TrackableList>
                options={faceLists}
                value={selectedList}
                onChange={(value) => {
                  setSelectedList(value as TrackableList | null);
                  setSelectedItem(null);
                }}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                isLoading={isLoadingLists}
                placeholder="Select a face watchlist..."
                isDisabled={isSubmitting || !canUseExistingLists}
              />
            ) : (
              <Input
                value={newListName}
                onChange={(event) => setNewListName(event.target.value)}
                placeholder="New watchlist name..."
                disabled={isSubmitting}
                autoFocus
              />
            )}
          </div>
        </Field>

        <Field label="Watchlist item">
          <div className="flex flex-col gap-2">
            <Radio
              id="add-face-item-mode"
              value={itemMode}
              onChange={(value) => {
                if (value === 'existing' || value === 'new') {
                  setItemMode(value);
                }
              }}
              disabled={isSubmitting || listMode === 'new'}
            >
              <RadioOption
                value="existing"
                disabled={listMode === 'new' || !selectedList}
              >
                Existing item
              </RadioOption>
              <RadioOption value="new">New item</RadioOption>
            </Radio>

            {itemMode === 'existing' && listMode === 'existing' ? (
              <>
                <Select<TrackableItem>
                  options={items}
                  value={selectedItem}
                  onChange={(value) =>
                    setSelectedItem(value as TrackableItem | null)
                  }
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  formatOptionLabel={(option, { context }) => {
                    if (context === 'value') {
                      return <span>{option.name}</span>;
                    }
                    return (
                      <div className="flex flex-col gap-0.5 py-0.5">
                        <span className="text-sm font-medium">
                          {option.name}
                        </span>
                        {option.description && (
                          <span className="text-xs opacity-60">
                            {option.description}
                          </span>
                        )}
                        {(option.labels?.length ?? 0) > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {option.labels.map((label) => (
                              <Pill key={label} size="small">
                                {label}
                              </Pill>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }}
                  isLoading={isLoadingItems}
                  placeholder={
                    selectedList
                      ? 'Select an item...'
                      : 'Select a watchlist first'
                  }
                  isDisabled={isSubmitting || !selectedList}
                />

                {selectedItem && (
                  <ExistingReferenceFaces
                    isLoading={isLoadingExistingFiles}
                    files={existingFiles}
                  />
                )}
              </>
            ) : (
              <>
                <Input
                  value={newItemName}
                  onChange={(event) => setNewItemName(event.target.value)}
                  placeholder="New item name..."
                  disabled={isSubmitting}
                />
                <Input
                  value={newItemDescription}
                  onChange={(event) =>
                    setNewItemDescription(event.target.value)
                  }
                  placeholder="Description (optional)..."
                  disabled={isSubmitting}
                />
                <Input
                  value={newItemLabels}
                  onChange={(event) => setNewItemLabels(event.target.value)}
                  placeholder="Labels, comma separated (optional)..."
                  disabled={isSubmitting}
                />
              </>
            )}
          </div>
        </Field>

        {error && (
          <Text size="small" intent="danger">
            {error}
          </Text>
        )}

        <div className="mt-2 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            Add to watchlist
          </Button>
        </div>
      </form>
    </Dialog.Root>
  );
}

type ExistingReferenceFacesProps = {
  isLoading: boolean;
  files: {
    id: string;
    name: string;
    thumbnail_url?: string | null;
    data_url?: string | null;
  }[];
};

function ExistingReferenceFaces({
  isLoading,
  files,
}: ExistingReferenceFacesProps) {
  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Text size="small" className="opacity-50">
        Loading...
      </Text>
    );
  } else if (files.length === 0) {
    body = (
      <Text size="small" className="opacity-50">
        No reference faces yet.
      </Text>
    );
  } else {
    body = (
      <div className="flex flex-wrap gap-2">
        {files.map((referenceFile) => (
          <div
            key={referenceFile.id}
            className="h-[64px] w-[64px] shrink-0 overflow-hidden rounded-md border border-neutral-300 dark:border-neutral-700"
            title={referenceFile.name}
          >
            <img
              src={
                referenceFile.thumbnail_url ??
                referenceFile.data_url ??
                undefined
              }
              alt={referenceFile.name}
              className="h-full w-full object-cover"
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-1 flex flex-col gap-1">
      <Text size="small" className="opacity-60">
        Existing reference faces
      </Text>
      {body}
    </div>
  );
}
